import { Stack, SxProps, TextField, TextFieldProps } from '@mui/material'
import { useTranslation } from 'react-i18next'
import NumberFormat, { NumberFormatProps } from 'react-number-format'

import {
  Control,
  UseFormTrigger,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
  Path,
  useController,
  RegisterOptions,
} from 'react-hook-form'
import { FormValues } from '../'

import Label, { LabelProps } from './Label'

interface ConnectedTextfieldProps extends NumberFormatProps {
  onChange?: (arg: any) => void
  title?: string
  titleProps?: LabelProps
  label?: string
  placeholder?: string
  translateTitle?: boolean
  translateLabel?: boolean
  textfieldProps?: NumberFormatProps<TextFieldProps>
  sxProps?: SxProps
  thousandSeparator?: NumberFormatProps['thousandSeparator']
  control: Control<FormValues>
  trigger?: UseFormTrigger<FormValues>
  getValues?: UseFormGetValues<FormValues>
  resetField?: UseFormResetField<FormValues>
  setValue?: UseFormSetValue<FormValues>
  name: Path<FormValues>
  rules?: RegisterOptions
}

const ConnectedNumberField = (props: ConnectedTextfieldProps) => {
  const {
    title,
    titleProps,
    label,
    placeholder,
    translateTitle = false,
    translateLabel = false,
    sxProps,
    textfieldProps,
    thousandSeparator,
    control,
    name,
    rules,
  } = props
  const { t } = useTranslation()

  const {
    field,
    fieldState: { error },
  } = useController<FormValues>({
    name,
    control,
    rules: rules,
    defaultValue: '',
    shouldUnregister: true,
  })

  return (
    <Stack spacing={2}>
      {title ? (
        <Label
          label={title}
          translateLabel={translateTitle}
          required={false}
          {...titleProps}
        />
      ) : null}
      <NumberFormat
        autoComplete="off"
        decimalSeparator=","
        thousandSeparator={thousandSeparator ?? '.'}
        isNumericString
        thousandsGroupStyle="thousand"
        allowNegative={false}
        customInput={TextField}
        value={field.value}
        inputRef={field.ref}
        onBlur={field.onBlur}
        error={!!error}
        helperText={error?.message}
        variant="outlined"
        fullWidth
        label={
          translateLabel
            ? label
              ? t(label)
              : label
            : label
            ? label
            : undefined
        }
        placeholder={
          translateLabel
            ? placeholder
              ? t(placeholder)
              : placeholder
            : placeholder
            ? placeholder
            : undefined
        }
        onValueChange={(values) => {
          const { value } = values
          if (value !== '') {
            field.onChange(Number(value))
          } else {
            field.onChange(value)
          }
        }}
        sx={{
          ...sxProps,
        }}
        {...textfieldProps}
      />
    </Stack>
  )
}

export default ConnectedNumberField
